import { HttpErrorResponse, HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { Observable, catchError, switchMap, take, throwError } from 'rxjs';
import { API_BASE_URL } from '../config/api.config';
import { AuthService } from '../services/auth.service';

// Shared across all requests so concurrent 401s trigger a single refresh
// call instead of a stampede of parallel refresh attempts.
let refreshInFlight$: Observable<unknown> | null = null;

const AUTH_ENDPOINTS = ['/auth/login', '/auth/signup', '/auth/refresh'];

export const authInterceptor: HttpInterceptorFn = (req, next) => {
  const authService = inject(AuthService);

  if (!req.url.startsWith(API_BASE_URL)) {
    return next(req);
  }

  const token = authService.getAccessToken();
  const isAuthEndpoint = AUTH_ENDPOINTS.some((path) => req.url.includes(path));

  const authedReq = req.clone({
    withCredentials: true,
    setHeaders: token && !isAuthEndpoint ? { Authorization: `Bearer ${token}` } : {},
  });

  return next(authedReq).pipe(
    catchError((error: unknown) => {
      if (
        error instanceof HttpErrorResponse &&
        error.status === 401 &&
        !isAuthEndpoint
      ) {
        return attemptRefreshThenRetry(authService, authedReq, next);
      }
      return throwError(() => error);
    }),
  );
};

function attemptRefreshThenRetry(
  authService: AuthService,
  originalReq: Parameters<HttpInterceptorFn>[0],
  next: Parameters<HttpInterceptorFn>[1],
) {
  if (!refreshInFlight$) {
    refreshInFlight$ = authService.refreshAccessToken().pipe(
      catchError((refreshError) => {
        authService.clearSession();
        return throwError(() => refreshError);
      }),
    );
  }

  return refreshInFlight$.pipe(
    take(1),
    switchMap(() => {
      refreshInFlight$ = null;
      const newToken = authService.getAccessToken();
      const retryReq = originalReq.clone({
        setHeaders: newToken ? { Authorization: `Bearer ${newToken}` } : {},
      });
      return next(retryReq);
    }),
    catchError((error) => {
      refreshInFlight$ = null;
      return throwError(() => error);
    }),
  );
}
